// Dependencies
import { format, parseISO } from "date-fns";
import { Map } from "immutable";
import React, { useState } from "react";
import { connect } from "react-redux";

import { faMessageDots, faPencil, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { Spinner } from "@performyard/py-components";

// components
import UserBlock from "./components/UserBlock";

import { useFeedbackDrawerStore } from "../../containers/FeedbackDrawers/FeedbackDrawerStore";

// hooks
import { useSingleFeedback } from "../../api/hooks/defs/feedback";

// utils
import { DeleteFeedback } from "../../actions/feedback.actions";
import { DisplayConfirmationModal } from "../../actions/modals.actions";

import FeedbackTypes from "../../constants/FeedbackTypes";
import IconButton from "../Button/IconButton";
import Icon from "../Icon";
import ConfirmationModal from "../Modal/Templates/ConfirmationModal";
import Spacer from "../Spacer";
import Typography from "../Text";
import { TextEditor } from "../TextEditor";

export type FeedbackViewerProps = {
	feedbackId: string;
	isAdmin: boolean;
	deleteConfirmation: (message: string, callback: () => void) => void;
	deleteFeedback: (feedbackId: string) => void;
	userId: string;
	features: Map<string, any>;
	onDelete?: () => void;
};

export const FeedbackViewer = ({
	feedbackId,
	isAdmin,
	deleteFeedback,
	userId,
	features,
	onDelete,
}: FeedbackViewerProps) => {
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const { isLoading, data: currentFeedback } = useSingleFeedback(feedbackId);
	const { leaveFeedback } = useFeedbackDrawerStore();
	const getSharingString = () => {
		const selectedFeedback = currentFeedback;
		return (
			<div className="sharing-message">
				{FeedbackTypes.getIn([selectedFeedback?.shared_with, "shareDescription"]) as string}
			</div>
		);
	};

	const handleDelete = async () => {
		await deleteFeedback(feedbackId);
		onDelete && onDelete();
	};

	const handleEdit = async () => {
		leaveFeedback({ feedbackId });
	};

	const allowRequestFeedback = features.get("allowRequestFeedback");

	if (feedbackId === null) {
		return null;
	}

	if (isLoading || !currentFeedback) {
		return (
			<div className="flex h-full items-center justify-center text-center">
				<Spacer height={10} />
				<Spinner size={20} />
			</div>
		);
	}

	if (currentFeedback.deleted) {
		return (
			<div style={{ textAlign: "center" }}>
				<div
					style={{
						fontSize: 13,
						textAlign: "center",
						fontStyle: "italic",
					}}
				>
					Feedback has been removed. <a href="/">Go to dashboard</a>
				</div>
			</div>
		);
	}

	const subjects = currentFeedback.subjects_list?.map((subject) => ({
		id: subject._id,
		name: subject.name,
	}));
	const isFeedbackRequestResponse = currentFeedback.feedback_request;

	const isAuthor = currentFeedback.author_id === userId;

	// We are intentionally creating a new instance of the editor every time.
	// This is because the editor persists it's own state and it's easier to create a new instance than
	// go against recommended practices and reset the state manually.
	const getTextEditor = () => {
		const feedbackMessage = currentFeedback?.feedback_message;
		const content = feedbackMessage ? feedbackMessage : currentFeedback?.content;

		//random namespace to reset editor state every render
		const namespace = Math.random().toString(36);
		return <TextEditor key={namespace} initialValue={content} readOnly namespace={namespace} />;
	};

	const parsedDate = parseISO(currentFeedback.last_updated);

	return (
		<>
			<section className="cp-feedback-viewer viewer-content">
				<div className="feedback-field-box" data-testid="feedback-viewer-subjects">
					<Typography weight="semi-bold" size="paragraph-3">
						{subjects?.length === 1 ? "Subject" : "Subjects"}:
					</Typography>
					{subjects?.map((subject) => (
						<UserBlock key={subject.id} id={subject.id} name={subject.name} />
					))}
				</div>

				<div className="feedback-field-box" data-testid="feedback-viewer-author">
					<Typography weight="semi-bold" size="paragraph-3">
						Author:
					</Typography>
					<UserBlock id={currentFeedback.author_id} name={currentFeedback.author_object?.name} />
				</div>

				<div className={`info-container`}>
					<div className="feedback-chips">
						{getSharingString()}
						{isFeedbackRequestResponse && allowRequestFeedback ? (
							<div className="sharing-message">Response to Request</div>
						) : null}
					</div>

					<div className="controls-box">
						{isAuthor && <IconButton onClick={handleEdit} icon={faPencil} aria-label="Edit feedback" />}
						{(isAuthor || isAdmin) && (
							<IconButton
								onClick={() => setIsDeleteModalOpen(true)}
								icon={faTrashCan}
								aria-label="Delete feedback"
							/>
						)}
					</div>
				</div>
				<div
					className={`main-feedback-content ${isFeedbackRequestResponse ? "feedback-response" : ""}`}
					data-testid="feedback-viewer-content"
				>
					{isFeedbackRequestResponse && allowRequestFeedback ? (
						<div className="response-label" data-testid="feedback-viewer-response-label">
							<Icon icon={faMessageDots} size="small" />
							<Typography size="caption" weight="semi-bold">
								Response:
							</Typography>
						</div>
					) : null}
					<div className="last-updated" data-testid="feedback-viewer-last-updated">
						<span>Last Updated:</span> {format(parsedDate, "MMMM d yyyy h:mm a")}
					</div>
					<div className="content" data-testid="feedback-viewer-content">
						{getTextEditor()}
					</div>
				</div>

				{isFeedbackRequestResponse && allowRequestFeedback && currentFeedback.feedback_request.message ? (
					<div className="request-feedback-message-container" data-testid="feedback-viewer-request-message">
						<Typography size="caption" weight="semi-bold" testID="feedbackRequestMessageLabel">
							Message Request:
						</Typography>
						<Typography
							className="request-content"
							size="paragraph-3"
							weight="regular"
							testID="feedbackRequestMessage"
						>
							{currentFeedback.feedback_request.message as string}
						</Typography>
					</div>
				) : null}
			</section>
			{isDeleteModalOpen && (
				<ConfirmationModal
					positiveActionLabel="Delete"
					positiveAction={handleDelete}
					negativeActionLabel="Cancel"
					negativeAction={() => setIsDeleteModalOpen(false)}
				>
					Are you sure you want to delete this feedback?
				</ConfirmationModal>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	userId: state.app.getIn(["user", "id"]),
	isAdmin: state.app.getIn(["user", "isAdmin"], false),
	features: state.app.getIn(["features", "feedback"], Map()),
});

const mapDispatchToProps = (dispatch) => ({
	deleteFeedback: async (feedbackId) => await dispatch(DeleteFeedback(feedbackId)),
	deleteConfirmation: (message, action) => dispatch(DisplayConfirmationModal(message, action)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackViewer);
